import i18n from 'i18next';
import {
    PanelGraphType,
    PanelIntervalType,
    PanelModelType,
    PanelSeriesType,
    PanelThresholdType,
    PanelType,
    RateGroupType,
    PanelLineCalculation,
    PanelAverageLineThresholdType,
    PanelColumnType,
    PanelColumnRangeCategory,
    PanelTheme,
} from 'types/graphql-global-types';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import { SelectWithSearchDataArrayInterface } from 'views/components/core/select-with-categories/select-with-categories.interface';

const PanelService = {
    getThresholdType: (triThreshold1Type: any, triThreshold2Type: any) => {
        if (
            triThreshold1Type === PanelThresholdType.SAMPLE_MEDIAN &&
            triThreshold2Type === PanelThresholdType.SAMPLE_MEAN
        )
            return PanelConstants.panelThresholdType.SAMPLE_MEDIAN_MEAN;
        if (
            triThreshold1Type === PanelThresholdType.STUDY_MEDIAN &&
            triThreshold2Type === PanelThresholdType.STUDY_MEAN
        )
            return PanelConstants.panelThresholdType.STUDY_MEDIAN_MEAN;
        if (
            triThreshold1Type === PanelThresholdType.STUDY_SPECIFIC &&
            triThreshold2Type === PanelThresholdType.STUDY_SPECIFIC
        )
            return PanelThresholdType.STUDY_SPECIFIC;
    },
    getGraphSeriesTypesOptions: () => {
        const graphSeriesTypes = Object.values(PanelSeriesType);
        const options = (graphSeriesTypes as any).map((type: any) => ({
            id: type,
            text: i18n.t(`admin:LINE_${type}`),
            active: true,
            default: false,
        }));
        options.unshift({ id: '', text: i18n.t('admin:selectSeriesTypes'), active: true, default: true });
        return options;
    },
    getIntervalTypeOptions: () => {
        const options = Object.values(PanelIntervalType).map((field: string) => ({
            id: field,
            text: i18n.t(`admin:${field}`),
            active: true,
            default: false,
        }));
        options.unshift({ id: '', text: i18n.t('admin:selectConfidenceIntervalTypeR'), active: true, default: true });
        return options;
    },
    getLineCalculationTypeOptions: () => {
        const options = Object.values(PanelLineCalculation).map((type: string) => ({
            id: type,
            text: i18n.t(`admin:${type}`),
            active: true,
            default: false,
        }));
        return options;
    },
    getAverageLineThresholdTypeOptions: () => {
        const options = Object.values(PanelAverageLineThresholdType).map((type: string) => ({
            id: type,
            text: i18n.t(`admin:${type}`),
            active: true,
            default: false,
        }));
        return options;
    },
    setPanelType: (value: any, panelType: any, panelState: any) => {
        const res = value || panelType;

        switch (res) {
            case PanelGraphType.SCATTER_PLOT_LINE:
                return PanelGraphType.SCATTER_PLOT_LINE;
            case PanelGraphType.HEATMAP:
                return PanelGraphType.HEATMAP;
            case PanelGraphType.COLUMN:
                return PanelGraphType.COLUMN;
            case PanelGraphType.DONUT:
                return PanelGraphType.DONUT;
            case PanelGraphType.STACKED_COLUMN:
                return PanelGraphType.STACKED_COLUMN;
            default:
                return panelState.graph.type;
        }
    },
    setRateGroupType: (panelModel: any) => {
        switch (panelModel) {
            case PanelConstants.modelOptions.NEGATIVE_BINOMIAL:
                return RateGroupType.NEGATIVE_BINOMIAL;
            default:
                return RateGroupType.POISSON;
        }
    },
    setModelParamsType: (panelModel: any) => {
        switch (panelModel) {
            case PanelConstants.modelOptions.RATE_GROUP:
                return PanelModelType.RATE_GROUP;
            case PanelConstants.modelOptions.RATE_GROUP_TREND:
                return PanelModelType.RATE_GROUP_TREND;
            case PanelConstants.modelOptions.NEGATIVE_BINOMIAL:
                return PanelModelType.RATE_GROUP;
            default:
                return panelModel;
        }
    },
    getPanelColumnType: (panelState: any) => {
        if (panelState.graph?.type === PanelGraphType.COLUMN) {
            switch (panelState.graph?.columnType) {
                case PanelColumnType.RANGED:
                    return PanelColumnType.RANGED;
                case PanelColumnType.STANDARD:
                    return PanelColumnType.STANDARD;
                default:
                    return '';
            }
        }
        return '';
    },
    getPanelSeriesType: (panelState: any) => {
        if (panelState.graph?.type === PanelGraphType.SCATTER_PLOT_LINE) {
            switch (panelState.graph?.seriesType) {
                case PanelSeriesType.EVENT:
                    return PanelSeriesType.EVENT;
                case PanelSeriesType.SITE:
                    return PanelSeriesType.SITE;
                case PanelSeriesType.SITE_CUMULATIVE:
                    return PanelSeriesType.SITE_CUMULATIVE;
                case PanelSeriesType.SITE_AVERAGE:
                    return PanelSeriesType.SITE_AVERAGE;
                case PanelSeriesType.SUBJECT:
                    return PanelSeriesType.SUBJECT;
                case PanelSeriesType.SUBJECT_BY_VISIT:
                    return PanelSeriesType.SUBJECT_BY_VISIT;
                default:
                    return '';
            }
        }
        return '';
    },
    getPanelColumnCategoryType: (panelState: any) => {
        if (
            panelState.graph?.type === PanelGraphType.COLUMN &&
            panelState.graph?.columnType === PanelColumnType.RANGED
        ) {
            switch (panelState.graph?.columnRangeCategory) {
                case PanelColumnRangeCategory.DATE:
                    return PanelColumnRangeCategory.DATE;
                case PanelColumnRangeCategory.NUMBER:
                    return PanelColumnRangeCategory.NUMBER;
                default:
                    return '';
            }
        }
        return '';
    },
    getPanelType: (panelState: any) => {
        if (panelState.panelType === PanelType.GRAPH)
            switch (panelState.graph.type) {
                default:
                    return '';
                case PanelGraphType.SCATTER_PLOT_LINE:
                    return PanelGraphType.SCATTER_PLOT_LINE;
                case PanelGraphType.HEATMAP:
                    return PanelGraphType.HEATMAP;
                case PanelGraphType.COLUMN:
                    return PanelGraphType.COLUMN;
                case PanelGraphType.DONUT:
                    return PanelGraphType.DONUT;
                case PanelGraphType.STACKED_COLUMN:
                    return PanelGraphType.STACKED_COLUMN;
            }
        if (panelState.panelType === PanelType.MODEL) return PanelType.MODEL;
        if (panelState.panelType === PanelType.TRI_OP_DATE) return PanelType.TRI_OP_DATE;
        if (panelState.panelType === PanelType.PROPORTIONS_OVER_TIME) return PanelType.PROPORTIONS_OVER_TIME;
        if (panelState.panelType === PanelType.TIMELINE) return PanelType.TIMELINE;
        if (panelState.panelType === PanelType.EVENT_RATES_OVER_TIME) return PanelType.EVENT_RATES_OVER_TIME;
        return '';
    },
    getPanelTheme: (panelTheme: PanelTheme) => {
        switch (panelTheme) {
            default:
                return '';
            case PanelTheme.THEME_1:
                return PanelTheme.THEME_1;
            case PanelTheme.THEME_2:
                return PanelTheme.THEME_2;
            case PanelTheme.THEME_3:
                return PanelTheme.THEME_3;
            case PanelTheme.THEME_4:
                return PanelTheme.THEME_4;
        }
    },
    setGraphType: (value: any, panelType: any) => {
        const res = value || panelType;
        switch (res) {
            case PanelGraphType.SCATTER_PLOT_LINE:
                return PanelType.GRAPH;
            case PanelGraphType.HEATMAP:
                return PanelType.GRAPH;
            case PanelGraphType.COLUMN:
                return PanelType.GRAPH;
            case PanelGraphType.DONUT:
                return PanelType.GRAPH;
            case PanelGraphType.STACKED_COLUMN:
                return PanelType.GRAPH;
            case PanelType.MODEL:
                return PanelType.MODEL;
            default:
                return '';
        }
    },
    getSeriesRowDataOptions: (): SelectWithSearchDataArrayInterface[] => {
        const options = PanelConstants.seriesRowData.map((dataRow) => ({
            id: dataRow,
            text: i18n.t(`admin:${dataRow}`),
            active: true,
            default: false,
        }));
        options.unshift({ id: '', text: i18n.t('admin:selectRowData'), active: true, default: true });
        return options;
    },
    getPanelModel: (modelParams: any): string => {
        switch (modelParams.type) {
            case PanelModelType.RATE_GROUP:
                if (modelParams?.rateGroupType === RateGroupType.NEGATIVE_BINOMIAL) {
                    return PanelConstants.modelOptions.NEGATIVE_BINOMIAL;
                }
                return PanelConstants.modelOptions.RATE_GROUP;
            case PanelModelType.RATE_GROUP_TREND:
                return PanelConstants.modelOptions.RATE_GROUP_TREND;
            default:
                return modelParams.type;
        }
    },
    checkIfNumber: (value: string) => {
        const isNumber = +value;
        if (isNumber !== Number(isNumber)) return value;
        return isNumber;
    },
};

export default PanelService;
